import { matchRoute } from "@softmax/web-addresses";
import { reservedRootSegments } from "@softmax/web-addresses/namespaces";
import { type GameAddressResolution } from "../../game-types";

const reservedRoots = new Set(reservedRootSegments);

const gameLookupRoots = new Set(["office", "sugarscape"]);

export type GamePagePath = {
  kind: "path";
  url: URL;
  route: ReturnType<typeof matchRoute>;
  tail: string[];
  unlisted: boolean;
  decodedSlug: string;
};

function isGameResourcePath(pathname: string): boolean {
  const route = matchRoute(pathname);
  return (
    route.area === "game" &&
    (route.name === "episode" ||
      route.name === "episodeReplay" ||
      route.name === "requestWatch" ||
      route.name === "lobby" ||
      route.name === "playSession")
  );
}

export function parseGamePagePath({
  href,
}: {
  href: string;
}): GamePagePath | GameAddressResolution {
  const requestUrl = new URL(window.location.href);

  if (!URL.canParse(href, requestUrl)) return { kind: "invalid" };

  const url = new URL(href, requestUrl);

  if (url.origin !== requestUrl.origin || url.username || url.password)
    return { kind: "invalid" };

  const route = matchRoute(url.pathname);

  if (route.name === "office.display") return { kind: "unmatched" };

  const segments = url.pathname.split("/").slice(1);

  if (segments.at(-1) === "") segments.pop();

  const unlisted = segments[0] === "unlisted";
  const [slug, ...tail] = unlisted ? segments.slice(1) : segments;

  if (
    !slug ||
    slug.startsWith("_") ||
    slug.includes(".") ||
    tail.at(-1)?.includes(".") ||
    (!unlisted && reservedRoots.has(slug) && !gameLookupRoots.has(slug)) ||
    isGameResourcePath(url.pathname)
  ) {
    return { kind: "unmatched" };
  }

  const decodedSlug = new URLSearchParams(
    `slug=${slug.replaceAll("&", "%26").replaceAll("+", "%2B")}`,
  ).get("slug")!;

  if (/%(?![0-9a-f]{2})/i.test(slug) || decodedSlug.includes("\uFFFD")) {
    return { kind: "invalid" };
  }

  return {
    kind: "path",
    url,
    route,
    tail,
    unlisted,
    decodedSlug,
  };
}
